import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Bell, X, Search, Check } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const LOCATION_PREFS_KEY = 'find2day_location_prefs_set';
const SUB_ID_KEY = 'find2day_sub_id';

/**
 * Iter 156 — Phase A.4: First-run region/village picker.
 *
 * Shows once when:
 *   - localStorage flag `find2day_location_prefs_set` is missing
 *   - the existing 3-step OnboardingPopup has been dismissed
 *   - the user is not on auth / admin / detail / business pages
 *
 * Also listens for `find2day:open-location-picker` so the Profile page (and
 * anything else) can re-open the modal to let the user edit their prefs.
 * When re-opened that way, existing prefs are pre-loaded from the API.
 *
 * On submit (or skip) the localStorage flag is set so it never appears
 * again on this device by itself.
 *
 * Region is hardcoded to Almería at launch.
 */
const RegionVillagePicker = () => {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [towns, setTowns] = useState([]);
  const [search, setSearch] = useState('');
  const [homeTown, setHomeTown] = useState('');
  const [followNearby, setFollowNearby] = useState(true);
  const [nearbyRadiusKm, setNearbyRadiusKm] = useState(25);
  const [followRegion, setFollowRegion] = useState(false);
  const [followedTowns, setFollowedTowns] = useState([]);
  const [extraTownSearch, setExtraTownSearch] = useState('');
  const [saving, setSaving] = useState(false);

  // Auto-open on mount has been intentionally REMOVED (Jun 2026) so new and
  // returning users are never interrupted by an automatic location pop-up.
  // The picker now only opens when the user explicitly asks for it via the
  // `find2day:open-location-picker` event (Profile page / TownPill). The
  // persistent, non-blocking TownPill already lets users set their town.

  // Listen for a re-open request from anywhere else in the app (e.g. Profile).
  useEffect(() => {
    const handler = async () => {
      // Pre-load existing prefs so editing feels seamless.
      const sub = localStorage.getItem(SUB_ID_KEY);
      if (sub) {
        try {
          const r = await axios.get(`${API}/api/notifications/location-prefs`, {
            params: { subscriptionId: sub },
          });
          const d = r.data || {};
          setHomeTown(d.homeTown || '');
          setFollowNearby(!!d.followNearby);
          setNearbyRadiusKm(Number(d.nearbyRadiusKm) || 25);
          setFollowRegion(!!d.followRegion);
          setFollowedTowns(Array.isArray(d.followedTowns) ? d.followedTowns : []);
        } catch (_e) { /* ignore — show blank */ }
      }
      setShow(true);
    };
    window.addEventListener('find2day:open-location-picker', handler);
    return () => window.removeEventListener('find2day:open-location-picker', handler);
  }, []);

  // Load town list when the modal opens.
  useEffect(() => {
    if (!show || towns.length) return;
    axios
      .get(`${API}/api/locations/almeria/towns`)
      .then((r) => setTowns(r.data?.towns || []))
      .catch(() => setTowns([]));
  }, [show, towns.length]);

  const filteredTowns = useMemo(() => {
    if (!search.trim()) return towns;
    const q = search.trim().toLowerCase();
    return towns.filter((tw) => tw.name.toLowerCase().includes(q));
  }, [towns, search]);

  const handleSkip = () => {
    localStorage.setItem(LOCATION_PREFS_KEY, '1');
    setShow(false);
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    const subscriptionId = localStorage.getItem(SUB_ID_KEY) ||
      `local-${crypto.randomUUID()}`;
    localStorage.setItem(SUB_ID_KEY, subscriptionId);
    // Feb 2026 — attach the auth header so the backend can stamp userId + email
    // on the device row. Without this the row was orphaned from the user's
    // account and the diagnostic reported "0 device_subscriptions" even though
    // the follow was saved.
    const token = (typeof localStorage !== 'undefined' && localStorage.getItem('token')) || '';
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      await axios.put(`${API}/api/notifications/location-prefs`, {
        subscriptionId,
        homeTown: homeTown || null,
        followedTowns: followedTowns.filter((t) => t && t !== homeTown),
        followRegion,
        followNearby: !!homeTown && followNearby,
        nearbyRadiusKm: Number(nearbyRadiusKm) || 25,
      }, { headers });
      localStorage.setItem(LOCATION_PREFS_KEY, '1');
      setShow(false);
      // Iter 164 — notify listeners (e.g. the TownPill) that prefs changed.
      window.dispatchEvent(new CustomEvent('find2day:location-prefs-saved'));
    } catch (_e) {
      // Even on failure, set the flag so we don't loop on broken networks.
      localStorage.setItem(LOCATION_PREFS_KEY, '1');
      setShow(false);
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[70] p-0 sm:p-4"
      data-testid="region-village-picker"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-100 text-purple-700">
              <MapPin className="h-4 w-4" aria-hidden="true" />
            </span>
            <h2 className="text-base font-bold text-gray-900" data-testid="picker-title">
              {t('locationPicker.title', 'Get alerts for your area')}
            </h2>
          </div>
          <button
            onClick={handleSkip}
            className="text-gray-400 hover:text-gray-600 p-1"
            data-testid="picker-close"
            aria-label={t('locationPicker.skip', 'Skip for now')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 overflow-y-auto flex-1">
          <p className="text-sm text-gray-600 mb-4">
            {t(
              'locationPicker.intro',
              'Pick your town in Almería so we can send you nearby events, offers, and safety alerts.'
            )}
          </p>

          {/* Town search */}
          <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">
            {t('locationPicker.homeTownLabel', 'Your town or village')}
          </label>
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('locationPicker.searchPlaceholder', 'Search Almería towns…')}
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              data-testid="picker-search"
              autoComplete="off"
            />
          </div>

          <div
            className="max-h-44 overflow-y-auto border border-gray-100 rounded-lg divide-y divide-gray-100 mb-4"
            data-testid="picker-towns-list"
          >
            {filteredTowns.length === 0 && (
              <div className="px-3 py-3 text-sm text-gray-400 text-center">
                {t('locationPicker.noTowns', 'No towns match your search.')}
              </div>
            )}
            {filteredTowns.map((tw) => (
              <button
                key={tw.name}
                type="button"
                onClick={() => setHomeTown(tw.name)}
                className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between hover:bg-purple-50 transition-colors ${
                  homeTown === tw.name ? 'bg-purple-50 text-purple-800 font-medium' : 'text-gray-800'
                }`}
                data-testid={`picker-town-${tw.name}`}
              >
                <span>{tw.name}</span>
                {homeTown === tw.name && <Check className="h-4 w-4 text-purple-600" />}
              </button>
            ))}
          </div>

          {/* Followed towns multi-select (optional) */}
          <div className="mb-4" data-testid="picker-followed-towns-section">
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">
              {t('locationPicker.followedTownsLabel', 'Also follow these towns (optional)')}
            </label>
            <p className="text-xs text-gray-500 mb-2">
              {t('locationPicker.followedTownsHint', 'Pick extra towns alongside your home town.')}
            </p>
            {/* Selected pills */}
            {followedTowns.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2" data-testid="picker-followed-pills">
                {followedTowns.map((tw) => (
                  <span
                    key={tw}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 text-xs font-medium"
                    data-testid={`picker-followed-pill-${tw}`}
                  >
                    {tw}
                    <button
                      type="button"
                      onClick={() => setFollowedTowns((prev) => prev.filter((x) => x !== tw))}
                      className="ml-0.5 -mr-0.5 p-0.5 rounded-full hover:bg-purple-200 transition-colors"
                      aria-label={t('locationPicker.removeTown', 'Remove')}
                      data-testid={`picker-followed-remove-${tw}`}
                    >
                      <X className="h-3 w-3" aria-hidden="true" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            {/* Add input */}
            <div className="relative">
              <input
                type="text"
                value={extraTownSearch}
                onChange={(e) => setExtraTownSearch(e.target.value)}
                placeholder={t('locationPicker.followedTownsAddPlaceholder', 'Add a town…')}
                className="w-full pl-3 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                data-testid="picker-followed-input"
                autoComplete="off"
              />
              {extraTownSearch.trim() && (
                <div
                  className="absolute z-10 left-0 right-0 mt-1 max-h-36 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg divide-y divide-gray-100"
                  data-testid="picker-followed-dropdown"
                >
                  {towns
                    .filter((tw) => tw.name.toLowerCase().includes(extraTownSearch.trim().toLowerCase()))
                    .filter((tw) => tw.name !== homeTown && !followedTowns.includes(tw.name))
                    .slice(0, 8)
                    .map((tw) => (
                      <button
                        key={tw.name}
                        type="button"
                        onClick={() => {
                          setFollowedTowns((prev) => [...prev, tw.name]);
                          setExtraTownSearch('');
                        }}
                        className="w-full text-left px-3 py-2 text-sm text-gray-800 hover:bg-purple-50 transition-colors"
                        data-testid={`picker-followed-add-${tw.name}`}
                      >
                        {tw.name}
                      </button>
                    ))}
                </div>
              )}
            </div>
          </div>

          {/* Nearby + region opt-ins */}
          <div className="space-y-3 mb-1">
            <label className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors">
              <input
                type="checkbox"
                checked={followNearby}
                onChange={(e) => setFollowNearby(e.target.checked)}
                disabled={!homeTown}
                className="mt-0.5 h-4 w-4 accent-purple-600"
                data-testid="picker-toggle-nearby"
              />
              <span className="flex-1 leading-tight">
                <span className="block text-sm font-medium text-gray-900">
                  {t('locationPicker.nearbyLabel', 'Also alert me about nearby towns')}
                </span>
                <span className="block text-xs text-gray-500 mt-0.5">
                  {t(
                    'locationPicker.nearbyHint',
                    'Within {{km}} km of your town. Adjust if you want.',
                    { km: nearbyRadiusKm }
                  )}
                </span>
                <input
                  type="range"
                  min="5"
                  max="50"
                  step="5"
                  value={nearbyRadiusKm}
                  onChange={(e) => setNearbyRadiusKm(Number(e.target.value))}
                  disabled={!homeTown || !followNearby}
                  className="w-full accent-purple-600 mt-2"
                  data-testid="picker-radius-slider"
                  aria-label={t('locationPicker.radiusAria', 'Nearby radius in kilometres')}
                />
              </span>
            </label>

            <label className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors">
              <input
                type="checkbox"
                checked={followRegion}
                onChange={(e) => setFollowRegion(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-purple-600"
                data-testid="picker-toggle-region"
              />
              <span className="flex-1 leading-tight">
                <span className="block text-sm font-medium text-gray-900 flex items-center gap-1.5">
                  <Bell className="h-3.5 w-3.5 text-amber-500" aria-hidden="true" />
                  {t('locationPicker.regionLabel', 'Alert me about all of Almería')}
                </span>
                <span className="block text-xs text-gray-500 mt-0.5">
                  {t(
                    'locationPicker.regionHint',
                    'Get province-wide events, large offers, and safety alerts.'
                  )}
                </span>
              </span>
            </label>
          </div>
        </div>

        {/* Footer actions */}
        <div className="px-5 py-3 border-t border-gray-100 flex gap-3">
          <button
            onClick={handleSkip}
            className="flex-1 py-2.5 text-sm text-gray-500 hover:text-gray-700 font-medium rounded-lg"
            data-testid="picker-skip"
            disabled={saving}
          >
            {t('locationPicker.skip', 'Skip for now')}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 py-2.5 bg-purple-600 text-white text-sm font-semibold rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="picker-save"
          >
            {saving ? t('locationPicker.saving', 'Saving…') : t('locationPicker.save', 'Save my area')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RegionVillagePicker;
